import type { CSSProperties } from "react";

type SkeletonProps = {
  className?: string;
  /** Inline width/height escape hatch for one-off sizes. */
  style?: CSSProperties;
  /**
   * `rounded` matches the app's standard 10px radius, `pill` is for chips and
   * buttons, `circle` for avatars.
   */
  shape?: "rounded" | "pill" | "circle";
};

/**
 * A single shimmering placeholder block.
 *
 * Uses theme tokens rather than fixed greys so the same component reads
 * correctly on the light public site and inside the dark-mode dashboard.
 * `animate-pulse` is automatically neutralised by the reduced-motion rule in
 * globals.css, so no extra guard is needed here.
 */
export default function Skeleton({
  className = "",
  style,
  shape = "rounded",
}: SkeletonProps) {
  const radius =
    shape === "circle"
      ? "rounded-full"
      : shape === "pill"
        ? "rounded-full"
        : "rounded-[10px]";

  return (
    <div
      aria-hidden="true"
      style={style}
      className={`animate-pulse bg-hover ${radius} ${className}`}
    />
  );
}

/**
 * A run of text lines, the last one short so it reads as a paragraph rather
 * than a solid block.
 */
export function SkeletonText({
  lines = 3,
  className = "",
}: {
  lines?: number;
  className?: string;
}) {
  return (
    <div className={`space-y-2 ${className}`}>
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton
          key={i}
          className={`h-3 ${i === lines - 1 ? "w-2/3" : "w-full"}`}
        />
      ))}
    </div>
  );
}
